/*
  Quantity Breaks (Bundle & Save): Drybae
  - Syncs the selected bundle quantity with the product form quantity input.
  - Supports per-item variant selectors: builds items[n][id]/items[n][quantity]
    hidden inputs (form-associated) so /cart/add receives multiple line items.
  - Recomputes displayed prices when the main variant changes (listens to the
    bubbling "change" event dispatched on input[name="id"] by the theme).
*/
if (!customElements.get('quantity-breaks')) {
  class QuantityBreaks extends HTMLElement {
    connectedCallback() {
      this.formId = this.dataset.formId;
      this.form = document.getElementById(this.formId);
      // Pack size: units contained in one purchased item (e.g. 3-panty pack)
      this.unitsPerItem = parseInt(this.dataset.unitsPerItem, 10) || 1;
      this.moneyFormat =
        (window.themeGlobalVariables &&
          window.themeGlobalVariables.settings.money_format) ||
        '${{amount}}';
      const variantsScript = this.querySelector('[data-qb-variants]');
      this.variants = variantsScript
        ? JSON.parse(variantsScript.textContent)
        : [];
      this.itemsContainer = this.querySelector('.qb-items-inputs');
      this.options = [...this.querySelectorAll('.qb-option')];
      this.radios = [...this.querySelectorAll('.qb-option-input')];
      this.selects = [...this.querySelectorAll('.qb-variant-select')];

      this.radios.forEach((radio) => {
        radio.addEventListener('change', () => {
          this.applyState();
          this.updateOptionPrices();
        });
      });
      this.selects.forEach((select) => {
        select.addEventListener('change', (e) => {
          e.target.dataset.userChanged = '1';
          this.updateSelectorThumb(e.target);
          this.applyState();
          this.updateOptionPrices();
        });
      });

      if (this.form) {
        this.form.addEventListener('change', (e) => {
          if (e.target.name === 'id') this.onMainVariantChange();
        });
        if (this.dataset.hideQuantity === 'true') {
          const qtyWrapper = this.getQuantityInput()?.closest(
            '.product-form__quantity'
          );
          if (qtyWrapper) qtyWrapper.style.display = 'none';
        }
      }

      this.updateOptionPrices();
      this.applyState();
    }

    getQuantityInput() {
      if (!this.form) return null;
      const candidates = this.form.elements['quantity'];
      if (!candidates) return null;
      return candidates instanceof RadioNodeList || candidates.length
        ? candidates[0] || null
        : candidates;
    }

    getCurrentVariant() {
      const idInput = this.form && this.form.elements['id'];
      const id = idInput ? parseInt(idInput.value, 10) : NaN;
      return (
        this.variants.find((v) => v.id === id) || this.variants[0] || null
      );
    }

    getSelectedOption() {
      const checked = this.radios.find((r) => r.checked);
      return checked ? checked.closest('.qb-option') : null;
    }

    /* ------- cart payload ------- */
    applyState() {
      const option = this.getSelectedOption();
      if (!option || !this.form) return;
      const quantity = parseInt(option.dataset.quantity, 10) || 1;
      const selects = [
        ...option.querySelectorAll('.qb-variant-select:not([disabled])'),
      ];
      const currentId = this.form.elements['id']
        ? this.form.elements['id'].value
        : null;

      let ids = [];
      if (selects.length) {
        ids = selects.map((s) => s.value);
      }
      const mixed =
        ids.length > 0 && ids.some((id) => id !== (ids[0] || currentId));

      this.clearItemsInputs();
      const qtyInput = this.getQuantityInput();

      if (mixed) {
        // Multiple different variants: send items[] to /cart/add
        const grouped = {};
        ids.forEach((id) => {
          grouped[id] = (grouped[id] || 0) + 1;
        });
        let index = 0;
        Object.keys(grouped).forEach((id) => {
          this.appendItemInput(index, 'id', id);
          this.appendItemInput(index, 'quantity', grouped[id]);
          index++;
        });
        if (qtyInput) this.setInputValue(qtyInput, quantity);
      } else {
        // Single variant: native id + quantity flow
        if (ids.length && this.form.elements['id']) {
          this.form.elements['id'].value = ids[0];
        }
        if (qtyInput) this.setInputValue(qtyInput, quantity);
      }
    }

    setInputValue(input, value) {
      if (parseInt(input.value, 10) === value) return;
      input.value = value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    clearItemsInputs() {
      if (this.itemsContainer) this.itemsContainer.innerHTML = '';
    }

    appendItemInput(index, key, value) {
      if (!this.itemsContainer) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `items[${index}][${key}]`;
      input.value = value;
      input.setAttribute('form', this.formId);
      this.itemsContainer.appendChild(input);
    }

    /* ------- variant change from the main picker ------- */
    onMainVariantChange() {
      const variant = this.getCurrentVariant();
      if (!variant) return;
      this.selects.forEach((select) => {
        if (select.dataset.userChanged === '1') return;
        const match = [...select.options].find(
          (opt) => parseInt(opt.value, 10) === variant.id
        );
        if (match && !match.disabled) {
          select.value = match.value;
          this.updateSelectorThumb(select);
        }
      });
      this.updateOptionPrices();
      this.applyState();
    }

    /* ------- variant thumbnail next to the #n selector ------- */
    updateSelectorThumb(select) {
      const img = select
        .closest('.qb-selector-item')
        ?.querySelector('.qb-selector-item__img');
      if (!img) return;
      const variant = this.variants.find(
        (v) => v.id === parseInt(select.value, 10)
      );
      if (variant && variant.image) img.src = variant.image;
    }

    /* ------- price rendering ------- */
    updateOptionPrices() {
      const variant = this.getCurrentVariant();
      if (!variant) return;
      let selectedFinal = null;

      this.options.forEach((option) => {
        const quantity = parseInt(option.dataset.quantity, 10) || 1;
        const type = option.dataset.discountType || 'none';
        const value = parseFloat(option.dataset.discountValue) || 0;
        const selects = [
          ...option.querySelectorAll('.qb-variant-select:not([disabled])'),
        ];

        let base = 0;
        let compare = 0;
        if (selects.length === quantity && quantity > 0) {
          // Sum each selected item's own price
          selects.forEach((select) => {
            const v = this.variants.find(
              (item) => item.id === parseInt(select.value, 10)
            );
            const price = v ? v.price : variant.price;
            const comparePrice =
              v && v.compare_at_price && v.compare_at_price > v.price
                ? v.compare_at_price
                : price;
            base += price;
            compare += comparePrice;
          });
        } else {
          const comparePrice =
            variant.compare_at_price && variant.compare_at_price > variant.price
              ? variant.compare_at_price
              : variant.price;
          base = variant.price * quantity;
          compare = comparePrice * quantity;
        }

        let final = base;
        if (type === 'percentage') {
          final = Math.round((base * (100 - value)) / 100);
        } else if (type === 'fixed_amount') {
          final = Math.max(0, Math.round(base - value * 100));
        } else if (type === 'fixed_price') {
          final = Math.max(0, Math.round(value * 100));
        }
        const saved = Math.max(0, compare - final);
        const radio = option.querySelector('.qb-option-input');
        if (radio && radio.checked) selectedFinal = final;

        // Fields with data-manual="true" hold merchant-entered text: never overwrite
        const priceEl = option.querySelector('.qb-price');
        if (priceEl && priceEl.dataset.manual !== 'true') {
          priceEl.innerHTML = this.formatMoney(final);
        }
        const compareEl = option.querySelector('.qb-compare');
        if (compareEl && compareEl.dataset.manual !== 'true') {
          compareEl.innerHTML =
            compare > final ? this.formatMoney(compare) : '';
        }
        const unitEl = option.querySelector('.qb-unit');
        if (unitEl && unitEl.dataset.manual !== 'true' && quantity > 0) {
          unitEl.innerHTML = `(${this.formatMoney(
            Math.round(final / (quantity * this.unitsPerItem))
          )} ${unitEl.dataset.unitLabel || '/ each'})`;
        }
        const captionEl = option.querySelector('.qb-caption');
        if (captionEl && captionEl.dataset.caption) {
          if (saved > 0) {
            const percent =
              compare > 0 ? Math.round((saved / compare) * 100) : 0;
            captionEl.innerHTML = captionEl.dataset.caption
              .replace('[saved]', `<strong>${this.formatMoney(saved)}</strong>`)
              .replace('[percent]', `<strong>${percent}%</strong>`);
            captionEl.hidden = false;
          } else {
            captionEl.hidden = true;
          }
        }
      });

      if (selectedFinal !== null) this.updateAtcPrice(selectedFinal);
    }

    /* ------- price chip inside the Add to Cart button ------- */
    updateAtcPrice(cents) {
      if (!this.form) return;
      const button =
        this.form.querySelector('[name="add"]') ||
        this.form.querySelector('button[type="submit"]');
      if (!button) return;
      let chip = button.querySelector('.qb-atc-price');
      if (!chip) {
        chip = document.createElement('span');
        chip.className = 'qb-atc-price';
        const textSpan = button.querySelector('span');
        if (textSpan && textSpan.parentElement === button) {
          textSpan.appendChild(chip);
        } else {
          button.appendChild(chip);
        }
      }
      const formatted = this.formatMoney(cents);
      if (chip.textContent !== formatted) {
        chip.textContent = formatted;
        chip.classList.remove('qb-atc-price--bump');
        void chip.offsetWidth;
        chip.classList.add('qb-atc-price--bump');
      }
    }

    formatMoney(cents) {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, this.moneyFormat);
      }
      return `$${(cents / 100).toFixed(2)}`;
    }
  }
  customElements.define('quantity-breaks', QuantityBreaks);
}
